import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isVOBofType, onOff } from '@iotv/iotv-v3-types';
import { Button, Checkbox, Stack, Switch, Typography } from '@mui/material';
import { useContext, useEffect, useReducer } from 'react';
import { NavSections } from '../../../../../../components/navigation/NavSections';
import { AppMainViewContext } from '../../../../../../ContextProviders/AppContextProvider';
import { DeviceControlDAO } from '../../../../../../data/DeviceControlDAO';
import { AppLocalStorage } from '../../../../../../data/LocalStorage/AppLocalStorage';
import { ActionIcons } from '../../../../../../Iconography';
import { defaults, defaultSettings, defaultState, reducer } from './CommonControlReducerContext';
import { BinaryControl } from './components/BinaryControl';
import { NumericControl } from './components/NumericControl';
import { OptionsBitMapControl } from './components/OptionsBitMapControl';
import { ValveActTypes } from '@iotv/lfc-devices';
const excludedKeys = ['privateNetwork', 'debug'];
const byteObjectRepresentationTemplate = Object.fromEntries(Object.entries(ValveActTypes.solenoidControllerOptionsTemplate).filter(([k, v]) => {
    return !excludedKeys.includes(k) && !k.toLowerCase().includes('rfu');
}));
export const useLFCDevicesCommonControls = ({ devices, config }) => {
    const [{ contextUser }] = useContext(AppMainViewContext);
    const [state, dispatch] = useReducer(reducer, defaultState);
    useEffect(() => {
        if (config) {
            const supportedUpdates = Object.entries({ ...config.requested, ...config.reported }).filter(([key]) => Object.keys(defaults).includes(key));
            supportedUpdates.map(([key, v]) => dispatch({ type: 'UpdateValue', update: { key, v } }));
        }
    }, [config]);
    const { payloadRequestObject, valueSettings } = state;
    const sendDownlinks = async ({ contextUser, devices, payload }) => {
        const res = { err: null, data: null };
        const typeFilter = (x) => isVOBofType('Device', x);
        const newStoredValues = { ...defaultSettings, ...payload };
        AppLocalStorage.set(NavSections.THINGVIEW, 'lfcSolenoidControllerSettings', newStoredValues);
        const { err, data: sendData } = await DeviceControlDAO.device.control.command.send({
            desiredPayload: payload,
            devices: devices.filter(typeFilter),
            requestInitiator: contextUser,
        });
        if (err) {
            res.err = err;
        }
        if (sendData) {
            res.data = sendData;
        }
        return res;
    };
    const canSend = Object.keys(payloadRequestObject).length > 0;
    const handleSend = async () => {
        if (contextUser) {
            const { err, data } = await sendDownlinks({ contextUser, devices, payload: payloadRequestObject });
            if (err) {
                console.error(`ERR: sendDownlinks handleSend `, err.message, { err, data });
            }
            if (data) {
                dispatch({ type: 'ClearDownlinkObject', update: null });
            }
        }
    };
    const components = {
        appCycleTime: (_jsx(NumericControl, { state,
            dispatch,
            stateKey: 'appCycleTime',
            label: 'Application Cycle',
            maxWidth: '12em',
            unitLabel: valueSettings['appCycleTime'].value === 1 ? 'minute' : 'minutes' })),
        sysCycleTime: (_jsx(NumericControl, { state,
            dispatch,
            stateKey: 'sysCycleTime',
            label: 'System Cycle',
            maxWidth: '12em',
            unitLabel: valueSettings['sysCycleTime'].value === 1 ? 'minute' : 'minutes' })),
        accelerometerThreshold: (_jsx(NumericControl, { state, dispatch, stateKey: 'accelerometerThreshold', label: 'Accel. Threshold', maxWidth: '12em', unitLabel: '×16 mG' })),
        operateValve: (_jsxs(Stack, { direction: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '12em',
            boxSizing: 'border-box',
            paddingInline: 1.5,
            borderRadius: 1,
            border: (t) => `1px solid ${t.palette.action.disabled}`,
            sx: {
                [`&:hover`]: {
                    border: valueSettings.operateValve.include ? (t) => `1px solid ${t.palette.action.active}` : undefined,
                },
            }, children: [_jsx(Checkbox, { checked: valueSettings.operateValve.include,
                    sx: { padding: 0 },
                    onClick: () => {
                        dispatch({ type: 'TogglePresence', update: { key: 'operateValve' } });
                    } }), _jsx(Switch, { size: 'small',
                    disabled: !valueSettings.operateValve.include,
                    checked: valueSettings.operateValve.value === 1,
                    onChange: (_, checked) => {
                        dispatch({ type: 'UpdateValue', update: { key: 'operateValve', v: checked ? onOff.ON : onOff.OFF } });
                    } }), _jsx(Typography, { color: valueSettings.operateValve.include ? undefined : 'textDisabled', sx: { paddingBlock: 1 }, children: `Solenoid ${valueSettings.operateValve.value === 1 ? 'on' : 'off'}` })] })),
        valveOffPulses: (_jsx(NumericControl, { state,
            dispatch,
            stateKey: 'valveOnPulses',
            label: 'Valve On Pulses',
            maxWidth: '12em',
            unitLabel: valueSettings['valveOnPulses'].value === 1 ? 'pulse' : 'pulses' })),
        valveOnPulses: (_jsx(NumericControl, { state,
            dispatch,
            stateKey: 'valveOffPulses',
            label: 'Valve Off Pulses',
            maxWidth: '12em',
            unitLabel: valueSettings['valveOffPulses'].value === 1 ? 'pulse' : 'pulses' })),
        boostVoltage: _jsx(NumericControl, { state, dispatch, stateKey: 'boostVoltage', label: 'Boost Voltage', maxWidth: '12em', unitLabel: 'V' }),
        adjustTimeSecs: (_jsx(NumericControl, { state,
            dispatch,
            stateKey: 'adjustTimeSecs',
            label: 'Adjust Time',
            maxWidth: '12em',
            unitLabel: valueSettings['adjustTimeSecs'].value === 1 ? 'second' : 'seconds' })),
        buttonTime: (_jsx(NumericControl, { state,
            dispatch,
            stateKey: 'buttonTime',
            label: 'Button Time',
            maxWidth: '12em',
            unitLabel: valueSettings['buttonTime'].value === 1 ? 'minute' : 'minutes' })),
        sendConfig: _jsx(BinaryControl, { state, dispatch, stateKey: 'sendConfig', label: 'Send Schedules', maxWidth: '12em' }),
        sendSettings: _jsx(BinaryControl, { state, dispatch, stateKey: 'sendSettings', label: 'Send Settings', maxWidth: '12em' }),
        setOptions: (_jsx(OptionsBitMapControl, { byteObjectRepresentationTemplate: byteObjectRepresentationTemplate, state, dispatch, stateKey: 'setOptions', label: 'Change Options', maxWidth: '16em' })),
        resetAllPatterns: _jsx(BinaryControl, { state, dispatch, stateKey: 'resetAllPatterns', label: 'Reset Patterns', maxWidth: '12em' }),
        stopAllPatterns: _jsx(BinaryControl, { state, dispatch, stateKey: 'stopAllPatterns', label: 'Stop Programmes', maxWidth: '12em' }),
        restoreDefaults: _jsx(BinaryControl, { state, dispatch, stateKey: 'restoreDefaults', label: 'Restore Defaults', maxWidth: '12em' }),
        reboot: _jsx(BinaryControl, { state, dispatch, stateKey: 'reboot', label: 'Reboot', maxWidth: '12em' }),
        sendButton: (_jsx(Stack, { direction: 'row', children: _jsx(Button, { disabled: !canSend, variant: "contained", size: "large", color: "success", endIcon: _jsx(ActionIcons.Send, {}), onClick: () => handleSend(), children: devices.length > 1 ? `Send Group Command (${devices.length} devices)` : `Send Command` }) })),
    };
    return { components };
};
